import * as ToneJS from 'tone';
import { HorizontalNoteContainer } from "../model/containers";
import {
    MetronomeMarking,
    Note,
    NoteDuration,
    NoteVelocity,
    TimeSignature
} from "../model/core";
import {
    Midi,
    Track
} from '@tonejs/midi';
import { RydProject } from '../model/project';
import { InstrumentName } from 'soundfont-player';
import { mapToInstrument } from '../audio/instruments/utils';
import { useAudioContextStore } from '../stores/AudioContextStore';
import {
    ContextCell,
    ContextPlane
} from '../model/context';


export type RydBasicTrackMetadata = {
    trackName: string,
    hnc: HorizontalNoteContainer,
    // bpm: MetronomeMarking,
    // ts: TimeSignature,
    instrument?: InstrumentName
};

function sanitizeInstrumentName(name: string): InstrumentName {
    // very basic sanitization, just to map some common names to soundfont names.
    const t = name.replace(/\s+/g, '_').replace(/[\(\)]/g, '');
    // console.log('Sanitized instrument name:', name, 'to', t);
    return t as InstrumentName;
}

export function midiTrackToHNC(
    midi: Midi,
    track: Track
): RydBasicTrackMetadata {

    const hasEndOfTrackTicks = !!track.endOfTrackTicks;

    const ppq = midi.header.ppq;
    const hnc = new HorizontalNoteContainer({
        length: track.endOfTrackTicks ? track.endOfTrackTicks : 0,
        ppq: ppq
    });

    track.notes.forEach((n: { midi: number, ticks: number, durationTicks: number, velocity: number }) => {
        const note = Note.fromIndex(n.midi);
        const b = ToneJS.Ticks(n.ticks).toBarsBeatsSixteenths();

        // if we didn't find a value for endOfTrackTicks, we just set the length of the HNC to the maximum value of the notes.
        if (!hasEndOfTrackTicks) {
            hnc.setLength(Math.max(hnc.getLength(), n.ticks + n.durationTicks));
        }
        hnc.put(n.ticks, {
            step: n.ticks,
            payload: note,
            duration: new NoteDuration(n.durationTicks),
            velocity: n.velocity * 128 as NoteVelocity
        });
    });

    // for now we assume the beat is always marked for quarter notes. As PPQ is a quester note, we just use this value as resolution.
    const metronomeResolution = new NoteDuration(ppq);

    /**
     * midi.header.tempos : { bpm: number; ticks: number; time: number; }[]
     * midi.header.timeSignatures : { timeSignature: number[]; ticks: number; time: number; }[]
     * midi.header.keySignatures : { key: string; scale: string; ticks: number; time: number; }[]
     */
    return {
        trackName: track.name ? track.name : 'Unnamed Track',
        hnc: hnc,
        // bpm: midi.header.tempos.length > 0
        //     ? new MetronomeMarking(midi.header.tempos[0].bpm, metronomeResolution)
        //     : new MetronomeMarking(120, metronomeResolution),
        // ts: midi.header.timeSignatures.length > 0
        //     ? new TimeSignature(midi.header.timeSignatures[0].timeSignature[0], midi.header.timeSignatures[0].timeSignature[1])
        //     : new TimeSignature(4, 4),
        instrument: track.instrument.name ? sanitizeInstrumentName(track.instrument.name) : undefined
    };
}

export function readMidiFileAsHNCs(midi?: Midi): RydBasicTrackMetadata[] {
    return !!midi
        ? midi.tracks.map((track, index) => midiTrackToHNC(midi, track)).filter(hnc => hnc !== null)
        : [];
}

function readMidiMetronomeMarkings(midi?: Midi): {
    mm: MetronomeMarking,
    atTick: number
}[] {
    if (!midi) return [];
    return midi.header.tempos.map(t => {
        return {
            mm: new MetronomeMarking(0 | t.bpm, new NoteDuration(midi.header.ppq)),
            atTick: t.ticks
        };
    });
}

function tempoChangeDurations(
    project: RydProject,
    tempoChanges: { atTick: number }[]
): number[] {
    if (tempoChanges.length === 0) return [];
    const durations: number[] = [];
    for (let i = 0; i < tempoChanges.length - 1; i++) {
        durations.push(tempoChanges[i + 1].atTick - tempoChanges[i].atTick);
    }
    // last one goes to the end of the piece, we don't know where it is, so we just put a large number.
    durations.push(project.getLength() - tempoChanges[tempoChanges.length - 1].atTick);
    return durations;
}

function readMidiTimeSignatures(midi?: Midi): {
    ts: TimeSignature,
    atTick: number
}[] {
    if (!midi) return [];
    return midi.header.timeSignatures.map(t => {
        return {
            ts: new TimeSignature(t.timeSignature[0], t.timeSignature[1]),
            atTick: t.ticks
        };
    });
}

export function convertMidiToRydProject({
    midi,
    includedTracks,
}: {
    midi: Midi | undefined,
    includedTracks: number[],
}): RydProject {
    const tracks = readMidiFileAsHNCs(midi)
        .filter((_, index) => includedTracks.includes(index))
    const tempoChanges = readMidiMetronomeMarkings(midi);
    const timeSignatureChanges = readMidiTimeSignatures(midi);

    const project = RydProject.createForNewProject('Imported MIDI Project');
    // FIXME: set project length to the max length of all tracks by midi.duration
    project.setLength(tracks.map(hnc => hnc.hnc.getLength()).reduce((a, b) => Math.max(a, b), 0));
    let ppq = 1;
    tracks.forEach((hnc, i) => {
        project.put(i, hnc.hnc, { skipLengthCheck: true }, { name: hnc.trackName });
        const instrument = mapToInstrument({
            trackOutputType: 'SF2',
            sfInstrument: hnc.instrument,
            audioContext: useAudioContextStore.getState().audioContext!,
        });
        if (instrument) project.instrumentAssignments.set(i, instrument)

        project.mixerState.tracks.set(i, {
            id: i,
            name: hnc.trackName,
            gain: 0.7,
            pan: 0.0,
            muted: false,
            solo: false,
            onBus: true
        });
        project.uiState.tracks.get(i)!.collapsed = true;
        ppq = Math.max(ppq, hnc.hnc.getPPQ());
    });
    project.contextFrame = {
        grid: new ContextPlane(project.getLength(), 1),
        computed: {
            metronome: tracks.length > 0 ? tempoChanges[0].mm : new MetronomeMarking(120, new NoteDuration(16)),
            timeSignature: timeSignatureChanges.length > 0 ? timeSignatureChanges[0].ts : new TimeSignature(4, 4),
        }
    }

    const tempoDurations = tempoChangeDurations(project, tempoChanges);
    tempoChanges.forEach((tc, i) => {
        if (tempoDurations[i] <= 0) return; // skip zero-length tempo changes
        const cell = new ContextCell(tc.atTick, tempoDurations[i]);
        cell.properties.metronome = tc.mm;
        project.contextFrame.grid.addCellToRow(0, cell);
        project.contextFrame.computed.metronome = tc.mm;
    });
    const tsDurations = tempoChangeDurations(project, timeSignatureChanges);
    project.contextFrame.grid.addRow(); // add a second row for time signatures
    timeSignatureChanges.forEach((tsc, i) => {
        if (tempoDurations[i] <= 0) return; // skip zero-length tempo changes
        const cell = new ContextCell(tsc.atTick, tsDurations[i]);
        cell.properties.timeSignature = tsc.ts;
        project.contextFrame.grid.addCellToRow(1, cell);
        project.contextFrame.computed.timeSignature = tsc.ts;
    });
    // set zoom factor to ppq for a 1:1 mapping of PPQ to pixels.

    project.uiState.zoomFactor = ppq;
    project.bookmarks.find(b => b.label === 'end' && b.scope === 'app')!.position = project.getLength();

    return project;
}
